import {BaseObject} from "@/common/BaseObject";
import {alias, createModelSchema, custom, deserialize, SKIP} from "serializr";
import {Schema} from "@/common/Schema";
import {RedactorUtils} from "@/modules/redactor/core/RedactorUtils";
import {ItemGroupModel} from "@/modules/redactor/models/item/core/ItemGroupModel";
import {Point} from "@/modules/redactor/models/utils/Point";

export class ItemKindModel extends BaseObject {
  constructor(
    key = null,
    code = null,
    group = null,
    priority = null,
    owners = [],
    ownersExclude = false,
    maps = [],
    mapsExclude = false,
    cellKinds = [],
    hasMergePrompt = false,
    posHero = null,
    levels = null,
    useCustomIcon = false,
  ) {
    super();

    this._key = key;
    this._code = code;
    this._group = group;
    this._priority = priority;
    this._owners = owners;
    this._ownersExclude = ownersExclude;
    this._maps = maps;
    this._mapsExclude = mapsExclude;
    this._cellKinds = cellKinds;
    this._hasMergePrompt = hasMergePrompt;
    this._posHero = posHero;
    this._levels = levels;
    this._useCustomIcon = useCustomIcon;
  }

  static schema() {
    return custom(
      (v) => {
        return v && v.key ? v.key : SKIP;
      },

      v => {
        if (typeof v === 'string') {
          return RedactorUtils.getItemKindByKey(v);
        } else if (typeof v === 'object' && v !== null) {
          return deserialize(ItemKindModel, v);
        } else {
          return null;
        }
      }
    );
  }

  static is(value) {
    return value instanceof ItemKindModel &&
      RedactorUtils.getItemKinds().some(v => v.key === value.key);
  }

  static isAny(value) {
    return value instanceof ItemKindModel && value.key === "it_any";
  }

  static isNone(value) {
    return value instanceof ItemKindModel && value.key === "none";
  }

  static getNone(items) {
    return items.find(item => item?.key === 'none')?.key;
  }

  static isEmpty(val) {
    return val === '' || val === null || val === undefined;
  }

  get key() {
    return this._key;
  }

  set key(value) {
    this._key = value;
  }

  get code() {
    return this._code;
  }

  set code(value) {
    this._code = value;
  }

  get group() {
    return this._group;
  }

  set group(value) {
    this._group = value;
  }

  get priority() {
    return this._priority;
  }

  set priority(value) {
    this._priority = value;
  }

  get owners() {
    return this._owners;
  }

  set owners(value) {
    this._owners = value;
  }

  get ownersExclude() {
    return this._ownersExclude;
  }

  set ownersExclude(value) {
    this._ownersExclude = value;
  }

  get maps() {
    return this._maps;
  }

  set maps(value) {
    this._maps = value;
  }

  get cellKinds() {
    return this._cellKinds;
  }

  set cellKinds(value) {
    this._cellKinds = value;
  }

  get hasMergePrompt() {
    return this._hasMergePrompt;
  }

  set hasMergePrompt(value) {
    this._hasMergePrompt = value;
  }

  get posHero() {
    return this._posHero;
  }

  set posHero(value) {
    this._posHero = value;
  }

  get levels() {
    return this._levels;
  }

  set levels(value) {
    this._levels = value;
  }

  get useCustomIcon() {
    return this._useCustomIcon;
  }

  set useCustomIcon(value) {
    this._useCustomIcon = value;
  }

  get mapsExclude() {
    return this._mapsExclude;
  }

  set mapsExclude(value) {
    this._mapsExclude = value;
  }

  valid(level = null) {
    if (typeof this.key !== "string" || !this.key.trim()) {
      return false;
    }

    if (!Number.isInteger(this.code) || this.code < 0) {
      return false;
    }

    if (!Number.isInteger(this.priority) || this.priority < 0) {
      return false;
    }

    if (!Array.isArray(this.owners)) {
      return false;
    }

    if (!Array.isArray(this.maps) || this.maps.length === 0 || this.maps.some(item => !Number.isInteger(item) || item < 0)) {
      return false;
    }

    if (this.posHero && !this.posHero.valid()) {
      return false;
    }

    if (level !== null) {
      if (Array.isArray(this.levels) && this.levels.length > 0) {
        const maxAllowed = Math.max(...this.levels);

        if (level > maxAllowed) {
          console.log('Level too high:', level, '>', maxAllowed);
          return false;
        }
      }
    }

    return true;
  }

  equal(value) {
    if (!(value instanceof ItemKindModel)) return false;
    return this.key === value.key;
  }

  copy() {
    let value = super.copy();
    value._enabled = false;
    return value;
  }

  clone(target = undefined) {
    if (!target) {
      return super.clone(new ItemKindModel());
    } else if (target instanceof ItemKindModel) {
      return super.clone(target);
    } else {
      console.log(false);
      return undefined;
    }
  }

  empty() {
    return typeof this.key !== 'string' || this.key.trim() === '';
  }
}

createModelSchema(ItemKindModel, {
  _key: alias("key", Schema.primitive()),
  _code: alias("code", Schema.number()),
  _group: alias("group", ItemGroupModel.schema()),
  _priority: alias("priority", Schema.number()),
  _owners: alias("owners", Schema.primitive()),
  _ownersExclude: alias("owners_exclude", Schema.primitive()),
  _maps: alias("maps", Schema.primitive()),
  _mapsExclude: alias("maps_exclude", Schema.primitive()),
  _cellKinds: alias("cell_kinds", Schema.primitive()),
  _hasMergePrompt: alias("has_merge_prompt", Schema.primitive()),
  _posHero: alias("pos_hero", Point.schema()),
  _levels: alias("levels", Schema.primitive()),
  _useCustomIcon: alias("use_custom_icon", Schema.primitive()),
});